import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    resolveProductDetails,
    getProductName,
    groupSupplementsByCategory,
    applyCategoryCutoff,
    normalizeEntrySource,
} from '../util/ProtocolUtil';
import { latestTestDate } from '../util/GenericUtil';
import { calculateDose } from '../util/RecUtil';
import {
    canEditEntry,
    canRemoveEntry,
    canAddEntries,
    isEntryLocked,
} from '../util/protocolPermissions';

/**
 * Shared protocol editing state and handlers used by both
 * DesktopProtocol and MobileProtocol.
 *
 * All items (generated + manual) are merged into a single `allItems`
 * array so drag-and-drop reordering works across both types.
 *
 * Author vs source model: each entry carries TWO orthogonal fields — `author`
 * (`doctor | patient`, WHO is responsible; drives lock/edit/remove) and `source`
 * (`generated | manual`, HOW it was created; provenance). On load, saved
 * entries are migrated to this model and classified as "manual" by PROVENANCE
 * (`source !== 'generated'`); the generated set is derived live from the
 * fresh recommendations rather than from the persisted entries.
 *
 * Edit re-stamp: a session `editedKeys` set records entries the user actually
 * edited (dose/frequency/timing/instructions/alternative). On save those entries
 * get `author` re-stamped to the current actor; all others keep their author.
 *
 * Regenerate merge: the Replace/Add choice governs ONLY the generated set; manual
 * contributions (and their dose edits / order) are always preserved. 'replace'
 * (default, matches prior behavior) takes the generated set entirely from the
 * fresh categoryGroups; 'add' additionally retains prior generated categories that
 * are absent from the fresh recommendations.
 */
export function useProtocolEditor({
    recommendations,
    recommendationMode,
    selectedAlternatives,
    handleSelectAlternative,
    savedProtocol,
    saveProtocol,
    bloodTestFiles,
    protocolLocked,
    doctorViewMode,
    effectiveLocked,
    hasActiveDoctor,
    regenerateMode = 'replace',
}) {
    const [saving, setSaving] = useState(false);
    const [doseEdits, setDoseEdits] = useState({});
    const [itemOrder, setItemOrder] = useState(null);
    const [patientNotes, setPatientNotes] = useState('');
    const [doctorNotes, setDoctorNotes] = useState('');
    const [manualEntries, setManualEntries] = useState([]);
    const [manualInterventions, setManualInterventions] = useState([]);
    const [removedCategories, setRemovedCategories] = useState(new Set());

    // Session set of entry keys the user actually edited this session. Used to
    // re-stamp `author` to the current actor on save. CRITICAL: doseEdits is
    // seeded from saved values on load, so presence in doseEdits is NOT evidence
    // of a session edit — only an explicit user action adds to editedKeys.
    const editedKeysRef = useRef(new Set());

    // The effective lock used for gating. Falls back to the legacy rule until the
    // views thread effectiveLocked from useProtocol.
    const effLocked = effectiveLocked != null
        ? effectiveLocked
        : (protocolLocked && !doctorViewMode);

    // Group recommendations by category, apply cutoffs (shared with the
    // auto-save path in useRecommendations so saved protocol matches display).
    // Cutoff ratios come from RECOMMENDATION_DISPLAY.protocol via applyCategoryCutoff.
    const categoryGroups = useMemo(() => {
        return applyCategoryCutoff(
            groupSupplementsByCategory(recommendations?.supplements || [])
        );
    }, [recommendations]);

    // Prior generated categories retained when regenerateMode === 'add'. DERIVED
    // (not state) so flipping replace<->add on the SAME protocol always
    // recomputes. The load-once effect early-returns on unchanged protocol
    // identity, so it must NOT own this value (doing so silently degraded
    // 'add' to 'replace' for the doctor-locked workflow).
    const savedBackendEntries = useMemo(() => {
        if (!savedProtocol?.entries?.length) return [];
        return savedProtocol.entries
            .map(e => normalizeEntrySource(e, savedProtocol))
            .filter(e => e.type !== 'intervention' && e.source === 'generated');
    }, [savedProtocol]);
    const retainedBackend = useMemo(
        () => (regenerateMode === 'add' ? savedBackendEntries : []),
        [regenerateMode, savedBackendEntries]
    );

    // Filtered generated groups (removed categories excluded). In 'add' mode,
    // prior generated categories absent from the fresh recommendations are retained
    // by synthesizing a fallback group from the saved entry so they keep showing.
    const displayGroups = useMemo(() => {
        const fresh = categoryGroups.filter(g => !removedCategories.has(g.category));
        if (!retainedBackend.length) return fresh;
        const freshCats = new Set(fresh.map(g => g.category));
        const retainedGroups = retainedBackend
            .filter(e => !freshCats.has(e.category) && !removedCategories.has(e.category))
            .map(e => ({
                category: e.category,
                weight: 0,
                products: [{
                    name: e.name,
                    product_details: { product_name: e.name, min_daily_dose: e.dose, unit: e.unit, freq_unit: e.freqUnit, empty_stomach: e.emptyStomach },
                    total_weight: 0,
                }],
            }));
        return [...fresh, ...retainedGroups];
    }, [categoryGroups, removedCategories, retainedBackend]);

    // Unified render list: generated + manual, in saved order
    const allItems = useMemo(() => {
        const generatedByKey = new Map(
            displayGroups.map(g => [g.category, { type: 'generated', key: g.category, group: g }])
        );
        const manualByKey = new Map(
            manualEntries.map(e => [`manual-${e.name}`, { type: 'manual', key: `manual-${e.name}`, entry: e }])
        );
        const interventionByKey = new Map(
            manualInterventions.map(e => [`intervention-${e.name}`, { type: 'intervention', key: `intervention-${e.name}`, entry: e }])
        );
        const allByKey = new Map([...generatedByKey, ...manualByKey, ...interventionByKey]);

        if (itemOrder) {
            const ordered = itemOrder.map(key => allByKey.get(key)).filter(Boolean);
            const orderedKeys = new Set(ordered.map(item => item.key));
            // Append any new items not yet in the order
            for (const [key, item] of allByKey) {
                if (!orderedKeys.has(key)) ordered.push(item);
            }
            return ordered;
        }

        return Array.from(allByKey.values());
    }, [displayGroups, manualEntries, manualInterventions, itemOrder]);

    // LOAD-ONCE from saved protocol, keyed on protocol identity. Keying on the
    // protocol (NOT on categoryGroups) is what fixes the clobber: regenerating
    // mutates categoryGroups but must not reset in-session edits/removals. The
    // separate "append new generated categories" behavior lives in allItems.
    const loadedKey = useRef(null);
    useEffect(() => {
        // Stable identity: lastEditedAt when present, else a signature of the
        // entry names. Changes when switching patients/protocols; stays constant
        // when only the recommendations prop changes for the same protocol.
        const protocolKey = savedProtocol
            ? (savedProtocol.lastEditedAt
                || JSON.stringify((savedProtocol.entries || []).map(e => e.name)))
            : null;
        if (protocolKey === loadedKey.current) return;
        loadedKey.current = protocolKey;
        // New protocol identity -> clear any session edit tracking.
        editedKeysRef.current = new Set();

        setPatientNotes(savedProtocol?.patientNotes || '');
        setDoctorNotes(savedProtocol?.doctorNotes || '');
        // Reconcile prior user removals from the saved protocol ITSELF: generated
        // categories present in its own rawRecommendations but absent from its
        // saved entries were removed by the user, so keep them removed on reload
        // (otherwise removing+saving a generated entry would resurrect it at the
        // bottom). This compares within the saved generation only — it does NOT
        // read the live recommendations prop, so it stays a true load-once
        // computation (clobber stays fixed) and a disjoint pre-rename protocol
        // filters nothing (stale "only 3 supplements" bug stays fixed).
        // Compare against the categories that were VISIBLE when the protocol was
        // saved, not the full candidate pool. The auto-save path deliberately
        // persists only the categories surviving applyCategoryCutoff while
        // storing the complete rawRecommendations, so diffing against the raw
        // pool misreads every below-cutoff category as a user removal and
        // suppresses it from all future generations.
        const savedBackendCats = new Set(savedBackendEntries.map(e => e.category));
        const savedVisibleCats = applyCategoryCutoff(
            groupSupplementsByCategory(savedProtocol?.rawRecommendations?.supplements || [])
        ).map(g => g.category);
        setRemovedCategories(new Set(savedVisibleCats.filter(c => !savedBackendCats.has(c))));

        if (savedProtocol?.entries?.length) {
            const edits = {};
            const manual = [];
            const interventions = [];
            savedProtocol.entries.forEach(rawEntry => {
                const entry = normalizeEntrySource(rawEntry, savedProtocol);
                const isIntervention = entry.type === 'intervention';
                const isManual = entry.source !== 'generated';
                if (isIntervention) {
                    interventions.push(entry);
                } else if (isManual) {
                    manual.push(entry);
                }
                const key = isIntervention
                    ? `intervention-${entry.name}`
                    : isManual ? `manual-${entry.name}` : entry.category;
                edits[key] = {
                    dose: entry.dose,
                    frequency: entry.frequency,
                    emptyStomach: entry.emptyStomach,
                    timeOfDay: entry.timeOfDay || null,
                    instructions: entry.instructions,
                };
            });
            setDoseEdits(edits);
            setManualEntries(manual);
            setManualInterventions(interventions);
            // Unified order: all entries in saved order
            setItemOrder(savedProtocol.entries.map(rawEntry => {
                const entry = normalizeEntrySource(rawEntry, savedProtocol);
                if (entry.type === 'intervention') return `intervention-${entry.name}`;
                return entry.source !== 'generated' ? `manual-${entry.name}` : entry.category;
            }));
        } else {
            setDoseEdits({});
            setManualEntries([]);
            setManualInterventions([]);
            setItemOrder(null);
        }
    }, [savedProtocol, savedBackendEntries]);

    // --- Dose editing ---
    const handleDoseEdit = (key, edit) => {
        // An actual user edit -> mark for author re-stamp on save.
        editedKeysRef.current.add(key);
        setDoseEdits(prev => ({ ...prev, [key]: edit }));
    };

    const handleSelectAlternativeAndReset = (category, productName) => {
        // Switching the alternative product is an edit -> re-stamp on save.
        editedKeysRef.current.add(category);
        handleSelectAlternative(category, productName);
        setDoseEdits(prev => {
            const { [category]: _, ...rest } = prev;
            return rest;
        });
    };

    // --- Add / Remove / Reorder ---
    // Stamp author = acting role and default source to 'manual' for any creation
    // path that did not set them (interventions, custom adds, legacy callers).
    // catalogToEntry already sets { source: 'manual', author }.
    const handleAddSupplement = (entry) => {
        const role = doctorViewMode ? 'doctor' : 'patient';
        setManualEntries(prev => [...prev, { ...entry, source: entry.source ?? 'manual', author: entry.author ?? role }]);
        // New entry appended to order automatically (not in itemOrder → allItems appends it)
    };

    const handleAddIntervention = (entry) => {
        const role = doctorViewMode ? 'doctor' : 'patient';
        setManualInterventions(prev => [...prev, { ...entry, source: entry.source ?? 'manual', author: entry.author ?? role }]);
    };

    const handleRemove = (key) => {
        if (key.startsWith('intervention-')) {
            const name = key.slice(13);
            setManualInterventions(prev => prev.filter(e => e.name !== name));
        } else if (key.startsWith('manual-')) {
            const name = key.slice(7);
            setManualEntries(prev => prev.filter(e => e.name !== name));
        } else {
            setRemovedCategories(prev => new Set(prev).add(key));
        }
        // Also remove from item order
        setItemOrder(prev => prev ? prev.filter(k => k !== key) : null);
    };

    const handleReorder = (fromIndex, toIndex) => {
        const currentKeys = allItems.map(item => item.key);
        const newKeys = [...currentKeys];
        const [moved] = newKeys.splice(fromIndex, 1);
        newKeys.splice(toIndex, 0, moved);
        setItemOrder(newKeys);
    };

    // --- Save ---
    const handleSave = async () => {
        setSaving(true);
        try {
            const actorRole = doctorViewMode ? 'doctor' : 'patient';
            // Generated entries from the saved protocol, keyed by category, so a
            // generated row can carry forward its existing author when unedited.
            const savedByCategory = new Map(
                (savedProtocol?.entries || [])
                    .map(e => normalizeEntrySource(e, savedProtocol))
                    .filter(e => e.source === 'generated' && e.type !== 'intervention')
                    .map(e => [e.category, e])
            );

            const entries = allItems.map(item => {
                if (item.type === 'generated') {
                    const group = item.group;
                    const selectedName = selectedAlternatives[group.category];
                    const product = selectedName
                        ? group.products.find(p => getProductName(p) === selectedName) || group.products[0]
                        : group.products[0];

                    const details = resolveProductDetails(product);
                    const defaultDose = calculateDose(details, product.total_weight || 0, recommendationMode);
                    const edit = doseEdits[group.category] || {};

                    const dose = edit.dose ?? defaultDose?.dose ?? 1;
                    const frequency = edit.frequency ?? defaultDose?.frequency ?? 1;

                    // Author: re-stamp to the current actor when this entry was
                    // edited this session; otherwise carry forward the prior
                    // author, defaulting to the current actor for fresh entries.
                    const prior = savedByCategory.get(group.category);
                    const author = editedKeysRef.current.has(group.category)
                        ? actorRole
                        : (prior?.author || actorRole);

                    return {
                        name: getProductName(product),
                        category: group.category,
                        dose,
                        frequency,
                        unit: details.unit || 'capsule',
                        freqUnit: details.freq_unit || 'day',
                        emptyStomach: edit.emptyStomach ?? details.empty_stomach ?? false,
                        timeOfDay: edit.timeOfDay || null,
                        instructions: edit.instructions || null,
                        notes: null,
                        source: 'generated',
                        author,
                    };
                } else {
                    const entry = item.entry;
                    const isIntervention = item.type === 'intervention';
                    const key = isIntervention ? `intervention-${entry.name}` : `manual-${entry.name}`;
                    const edit = doseEdits[key] || {};
                    const dose = edit.dose ?? entry.dose;
                    const frequency = edit.frequency ?? entry.frequency;
                    // Provenance (source) never changes on edit; default legacy/unset
                    // sources to 'manual'. Authorship is re-stamped to the current
                    // actor only when this entry was edited this session, else the
                    // existing author is preserved (default to actor when unset).
                    const source = entry.source || 'manual';
                    const author = editedKeysRef.current.has(key)
                        ? actorRole
                        : (entry.author || actorRole);
                    return {
                        ...entry,
                        dose,
                        frequency,
                        emptyStomach: isIntervention ? null : (edit.emptyStomach ?? entry.emptyStomach),
                        timeOfDay: edit.timeOfDay || entry.timeOfDay || null,
                        instructions: edit.instructions || entry.instructions || null,
                        source,
                        author,
                        ...(isIntervention ? { type: 'intervention' } : {}),
                    };
                }
            });

            // Same normalization as the generate path so testdateUsed is
            // always ISO YYYY-MM-DD, whichever path wrote it. Lab headers store
            // a compact YYYYMMDD, which would otherwise land here verbatim.
            const testdateUsed = latestTestDate(
                (bloodTestFiles || []).map(f => ({ testdate: f?.data?.header?.testdate }))
            );

            await saveProtocol({
                entries,
                patientNotes,
                doctorNotes,
                rawRecommendations: recommendations,
                testdateUsed,
            });
        } catch (err) {
            console.error('Error saving protocol:', err);
        } finally {
            setSaving(false);
        }
    };

    // Save notes only (locked patient — entries stay as doctor set them)
    const handleSaveNotes = async () => {
        setSaving(true);
        try {
            await saveProtocol({
                entries: savedProtocol?.entries || [],
                patientNotes,
                doctorNotes: savedProtocol?.doctorNotes || '',
                rawRecommendations: savedProtocol?.rawRecommendations || recommendations,
                testdateUsed: savedProtocol?.testdateUsed || null,
            });
        } catch (err) {
            console.error('Error saving notes:', err);
        } finally {
            setSaving(false);
        }
    };

    // Tracks effectiveLocked but stays backward-compatible for existing
    // consumers/tests that key off isLockedPatient.
    const isLockedPatient = effLocked && !doctorViewMode;
    const hasRecommendations = allItems.length > 0;
    const hasSavedEntries = savedProtocol?.entries?.length > 0;

    // --- Per-entry permission primitives (section 6 consumes these) ---
    const canEdit = useCallback(
        (entry) => canEditEntry(entry, { doctorViewMode, effectiveLocked: effLocked }),
        [doctorViewMode, effLocked]
    );
    const canRemove = useCallback(
        (entry) => canRemoveEntry(entry, { doctorViewMode, effectiveLocked: effLocked }),
        [doctorViewMode, effLocked]
    );
    const canAdd = useMemo(
        () => canAddEntries({ doctorViewMode, effectiveLocked: effLocked }),
        [doctorViewMode, effLocked]
    );
    const isLocked = useCallback(
        (entry) => isEntryLocked(entry, { effectiveLocked: effLocked }),
        [effLocked]
    );

    // Map a generated category to its persisted author so generated
    // rows can resolve author for lock/edit gating and the padlock. A generated
    // row that lacks a saved author yet (freshly generated) inherits the actor.
    const backendAuthorByCategory = useMemo(() => {
        const map = new Map();
        savedBackendEntries.forEach(e => { if (e.category) map.set(e.category, e.author); });
        return map;
    }, [savedBackendEntries]);

    // Resolve the effective author of an item for gating/padlock purposes.
    // Generated rows: saved author for the category, else the acting role.
    // Manual/intervention rows: the entry's own author.
    const resolveItemAuthor = useCallback((item) => {
        const actorRole = doctorViewMode ? 'doctor' : 'patient';
        if (item?.type === 'generated') {
            return backendAuthorByCategory.get(item.group?.category) || actorRole;
        }
        return item?.entry?.author || actorRole;
    }, [backendAuthorByCategory, doctorViewMode]);

    const existingNames = useMemo(() => {
        return allItems.map(item =>
            item.type === 'generated' ? getProductName(item.group.products[0]) : item.entry.name
        );
    }, [allItems]);

    // Build export list from saved protocol entries (source of truth for doses/additions/removals)
    const exportRecommendations = useMemo(() => {
        const entries = savedProtocol?.entries;
        if (!entries?.length) return [];
        return entries.map(entry => ({
            product_details: {
                product_name: entry.name,
                min_daily_dose: entry.dose,
                unit: entry.unit,
                freq_unit: entry.freqUnit,
                empty_stomach: entry.emptyStomach,
            },
            fulfills_categories: [{ category: entry.category, weight: 0 }],
            total_weight: 0,
            category: entry.category,
            doseOverride: {
                dose: entry.dose,
                frequency: entry.frequency,
                emptyStomach: entry.emptyStomach,
                instructions: entry.instructions || null,
            },
        }));
    }, [savedProtocol]);

    return {
        saving,
        doseEdits,
        patientNotes,
        setPatientNotes,
        doctorNotes,
        setDoctorNotes,
        allItems,
        existingNames,
        exportRecommendations,
        isLockedPatient,
        effectiveLocked: effLocked,
        canEdit,
        canRemove,
        canAdd,
        isLocked,
        resolveItemAuthor,
        hasRecommendations,
        hasSavedEntries,
        handleDoseEdit,
        handleSelectAlternativeAndReset,
        handleAddSupplement,
        handleAddIntervention,
        handleRemove,
        handleReorder,
        handleSave,
        handleSaveNotes,
    };
}
